import {
    CheckOutlined,
    CloseOutlined,
    CloudOutlined,
    LeftOutlined,
    RightOutlined,
    SwapOutlined,
} from "@ant-design/icons";
import { Carousel, Empty, Modal, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

/* =========================================================================
 *  MOCK DATA (preview local). Cuando se conecte a la API, sustituir por la
 *  información real del workshop.
 * =======================================================================*/
export const PROJECT_SHEET_MOCK = {
    title: "Agentic Test - Transformación inteligente del ciclo de nómina SAP",
    description:
        "Implementación de una solución empresarial basada en inteligencia artificial agéntica para la automatización del ciclo de nómina en SAP utilizando conectividad RFC.",
    actualKnowledge:
        "El proceso de nómina en SAP presenta una alta dependencia de operaciones manuales que demandan tiempo significativo y generan riesgos operativos como inconsistencias de datos y falta de trazabilidad.",
    problemSteps:
        "1. Las transacciones manuales para cada ciclo de nómina aumentan la probabilidad de errores humanos. 2. Los controles son reactivos y dificultan la prevención de problemas. 3. Las inconsistencias en datos maestros generan discrepancias difíciles de rastrear. 4. La trazabilidad limitada complica la identificación de puntos críticos.",
    agentObjective:
        "Automatizar integralmente el ciclo SAP vía RFC de manera autónoma, garantizando rapidez, precisión y mínima intervención manual.",
    architecture:
        "Azure OpenAI + AI Foundry + SAP RFC Connector + Cosmos DB.",
    volumetry:
        "31 áreas de nómina en estructura multinacional con alta variabilidad operativa.",
    dataSources: [
        "SAP RFC - PA03",
        "SAP RFC - PC00_M32_CALC",
        "Cosmos DB",
    ],
    agents: [
        {
            name: "PayrollOptimizer Agent",
            description:
                "Ejecuta los cálculos automáticos de nómina en SAP usando RFC.",
        },
        {
            name: "DataValidator Agent",
            description: "Audita y valida los datos maestros antes de cada ciclo.",
        },
        {
            name: "TraceabilityEnhancer Agent",
            description:
                "Supervisa procesos y registra trazabilidad completa de operaciones.",
        },
    ],
    metricsBefore: [
        {
            metric: "Tiempo total del ciclo",
            description: "Duración promedio antes de la automatización",
            volume: "11 días",
            variation: "",
        },
    ],
    metricsAfter: [
        {
            metric: "Tiempo total del ciclo",
            description: "Duración promedio tras los agentes",
            volume: "< 1 día",
            variation: "-90%",
        },
    ],
    hashTags: ["payroll", "SAP", "agentic"],
    idIndustry: "RRHH",
    idTypology: "Agentes + SAP RFC",
};

/* ---------- Paleta local de la ficha (acorde a las diapositivas) ---------- */
const C = {
    navy: "#0e2235",
    navySoft: "#1a3a5b",
    primary: "#143462",
    blue: "#1f8ee0",
    green: "#2bb673",
    orange: "#f0a43f",
    purple: "#7a5cff",
    text: "#1f2a44",
    textSoft: "#5b6b85",
    bgSoft: "#eef3f8",
    cardBorder: "#e6ecf2",
    white: "#ffffff",
};

/* ============================== Styled Wrappers ========================== */
const SlideFrame = styled.div`
  width: min(1520px, 92vw);
  aspect-ratio: 16 / 9;
  max-height: 86vh;
  background: #f3f6fa;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(20, 52, 98, 0.08);
  display: flex;
  flex-direction: column;
  font-family: "Camphor Pro", "Segoe UI", sans-serif;
  color: ${C.text};
`;

/* ----------------------------- SLIDE 1 ----------------------------------- */
const Slide1Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 32px 8px;
  background: #f3f6fa;
`;

const LogoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-left: 5px solid ${C.blue};
  padding-left: 12px;

  img {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
  span {
    font-weight: 700;
    color: ${C.primary};
    font-size: 22px;
    letter-spacing: -0.5px;
  }
  small {
    display: block;
    font-size: 9px;
    color: ${C.textSoft};
    letter-spacing: 1px;
  }
`;

const Eyebrow = styled.div`
  font-size: 12px;
  color: ${C.blue};
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const SlideTitle = styled.h1`
  margin: 0;
  font-size: 38px;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.1;
`;

const Pill = styled.div`
  background: ${(p) => p.$bg || C.blue};
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 700;
  letter-spacing: 1.5px;
  font-size: 11px;
`;

const FichaTag = styled.div`
  text-align: right;
  color: ${C.blue};
  font-size: 11px;
  margin-top: 4px;
  padding-right: 32px;
`;

const Slide1Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: 18px;
  padding: 14px 32px 24px;
`;

const HeroImage = styled.div`
  border-radius: 14px;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  background-color: ${C.navy};
  min-height: 220px;
`;

const MagnitudCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 22px 26px;
  box-shadow: 0 6px 18px rgba(20, 52, 98, 0.06);
  border-top: 4px solid ${C.navy};
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 14px;
`;

const StatItem = styled.div`
  .num {
    font-size: 30px;
    font-weight: 700;
    color: ${(p) => p.$color || C.blue};
    line-height: 1;
  }
  .lbl {
    font-size: 12px;
    font-weight: 700;
    color: ${C.text};
    margin-top: 6px;
  }
  .sub {
    font-size: 11px;
    color: ${C.textSoft};
  }
`;

const ThreeCols = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 6px 18px rgba(20, 52, 98, 0.06);
  display: flex;
  flex-direction: column;
  border-top: 4px solid ${(p) => p.$accent || C.blue};
  ${(p) =>
        p.$dark &&
        `background: ${C.navy}; color: #fff;
     box-shadow: 0 8px 24px rgba(20, 52, 98, 0.18);`}
`;

const CardLabel = styled.div`
  font-size: 11px;
  letter-spacing: 1.4px;
  font-weight: 700;
  color: ${(p) => p.$color || C.orange};
  text-transform: uppercase;
`;

const CardTitle = styled.h3`
  margin: 4px 0 8px;
  font-size: 16px;
  color: ${(p) => (p.$light ? "#fff" : C.navy)};
  font-weight: 700;
  line-height: 1.2;
`;

const BulletList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bullet = styled.li`
  display: flex;
  gap: 10px;
  font-size: 12.5px;
  color: ${(p) => (p.$light ? "rgba(255,255,255,0.85)" : C.text)};
  line-height: 1.35;

  .icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(p) => (p.$danger ? "#ffe4e1" : "rgba(31,142,224,0.12)")};
    color: ${(p) => (p.$danger ? "#d6435a" : C.blue)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 10px;
  }
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const AgentBadge = styled.div`
  background: #f4f8fc;
  border-radius: 10px;
  padding: 7px 10px;

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: ${C.navy};
    font-size: 12px;
  }
  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(p) => p.$dot || C.blue};
  }
  .desc {
    font-size: 10.5px;
    color: ${C.textSoft};
    margin-top: 3px;
    line-height: 1.25;
  }
`;

const CommBanner = styled.div`
  background: ${C.blue};
  color: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PoweredItem = styled.div`
  border-left: 3px solid ${(p) => p.$accent || C.blue};
  padding-left: 10px;
  margin-bottom: 10px;

  .name {
    color: #fff;
    font-weight: 700;
    font-size: 13px;
  }
  .desc {
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
  }
`;

const FooterBar = styled.div`
  height: 14px;
  background: ${C.blue};
`;

/* ----------------------------- SLIDE 2 ----------------------------------- */
const Slide2Header = styled.div`
  background: ${C.navy};
  color: #fff;
  padding: 26px 32px 22px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-left: 8px solid ${C.orange};
`;

const Slide2Body = styled.div`
  flex: 1;
  background: #f3f6fa;
  padding: 22px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(20, 52, 98, 0.06);
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background: ${C.navy};
  color: #fff;
  font-size: 12px;
  letter-spacing: 1.4px;
  font-weight: 700;
  text-transform: uppercase;

  > div {
    padding: 14px 18px;
    text-align: left;
  }
  > div.antes {
    background: ${C.blue};
  }
  > div.ahora {
    background: ${C.green};
  }
  > div.var {
    background: ${C.orange};
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  border-bottom: 1px solid #eef2f6;
  font-size: 13.5px;

  &:last-child {
    border-bottom: none;
  }

  > div {
    padding: 14px 18px;
    display: flex;
    align-items: center;
  }
  .metric {
    color: ${C.text};
    gap: 8px;
  }
  .antes {
    font-weight: 700;
    color: ${C.text};
  }
  .ahora {
    font-weight: 700;
    color: ${C.green};
  }
  .var {
    font-weight: 700;
    color: ${(p) => (p.$negative ? "#d6435a" : C.orange)};
  }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
`;

const KpiCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 18px 22px;
  border-left: 6px solid ${(p) => p.$accent || C.blue};
  box-shadow: 0 6px 18px rgba(20, 52, 98, 0.06);

  .big {
    font-size: 30px;
    font-weight: 700;
    color: ${(p) => p.$accent || C.blue};
    line-height: 1;
  }
  .lbl {
    font-size: 13px;
    font-weight: 700;
    color: ${C.text};
    margin-top: 6px;
  }
  .sub {
    font-size: 11.5px;
    color: ${C.textSoft};
    margin-top: 2px;
  }
`;

const Slide2Footer = styled.div`
  background: ${C.navy};
  color: #fff;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;

  .stack {
    color: ${C.blue};
    font-size: 11.5px;
  }
`;

/* ============================== Modal ==================================== */
const StyledModal = styled(Modal)`
  .ant-modal {
    max-width: 96vw;
  }
  .ant-modal-content {
    background: #f3f6fa;
    padding: 24px;
    border-radius: 22px;
  }
  .ant-modal-body {
    padding: 0;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  .slide-shell {
    display: flex;
    justify-content: center;
    padding: 8px 0;
  }

  .scaler {
    transform-origin: top center;
  }

  .ant-carousel .slick-dots li button {
    background: ${C.navy};
  }
  .ant-carousel .slick-dots li.slick-active button {
    background: ${C.blue};
  }
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$side === "left" ? "left: -8px;" : "right: -8px;")}
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: ${C.primary};
  color: #fff;
  cursor: pointer;
  font-size: 18px;
  z-index: 5;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);

  &:hover {
    background: ${C.blue};
  }
`;

/* ============================== Component ================================ */

const HERO_IMG =
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=900&q=80";

const dotColors = [C.blue, C.green, C.purple, C.orange];

function Slide1({ data }) {
    const agents = data.agents || [];
    const problems = (data.problemSteps || "")
        .split(/\d+\.\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 4);

    // Stats
    const beforeVol = data.metricsBefore?.[0]?.volume || "—";
    const afterVol = data.metricsAfter?.[0]?.volume || "—";

    return (
        <SlideFrame>
            <Slide1Header>
                <div>
                    <LogoBlock>
                        <img src="/logo192.png" alt="Sharksia" />
                        <div>
                            <span>Sharksia</span>
                            <small>SHARK BUSINESS · INNOVATION</small>
                        </div>
                    </LogoBlock>
                </div>
                <div style={{ flex: 1, padding: "0 24px" }}>
                    <Eyebrow>CASO DE USO · SHARKSIA × MICROSOFT AZURE</Eyebrow>
                    <SlideTitle>{data.title}</SlideTitle>
                </div>
                <div>
                    <Pill>ECOSISTEMA MULTIAGENTE</Pill>
                </div>
            </Slide1Header>
            <FichaTag>Ficha 1 / 2</FichaTag>

            <Slide1Body>
                <HeroImage $src={HERO_IMG} />

                <MagnitudCard>
                    <Eyebrow style={{ color: C.navy }}>MAGNITUD DE LA OPERACIÓN</Eyebrow>
                    <div
                        style={{
                            fontStyle: "italic",
                            fontSize: 12,
                            color: C.textSoft,
                            marginTop: 4,
                        }}
                    >
                        La complejidad operativa que estamos resolviendo
                    </div>
                    <StatRow>
                        <StatItem $color={C.navy}>
                            <div className="num">{beforeVol}</div>
                            <div className="lbl">tiempo previo</div>
                            <div className="sub">por ciclo</div>
                        </StatItem>
                        <StatItem $color={C.green}>
                            <div className="num">{afterVol}</div>
                            <div className="lbl">tiempo actual</div>
                            <div className="sub">con agentes</div>
                        </StatItem>
                        <StatItem $color={C.purple}>
                            <div className="num">{agents.length || 3}</div>
                            <div className="lbl">agentes</div>
                            <div className="sub">orquestados</div>
                        </StatItem>
                    </StatRow>
                </MagnitudCard>

                <ThreeCols>
                    {/* RETO */}
                    <InfoCard $accent={C.orange}>
                        <CardLabel $color={C.orange}>EL RETO</CardLabel>
                        <CardTitle>
                            {data.actualKnowledge
                                ? data.actualKnowledge.split(".")[0] + "."
                                : "Procesos manuales en cadena de suministro"}
                        </CardTitle>
                        <BulletList>
                            {problems.length > 0
                                ? problems.map((p, i) => (
                                    <Bullet key={i} $danger>
                                        <span className="icon">
                                            <CloseOutlined />
                                        </span>
                                        <span>{p}</span>
                                    </Bullet>
                                ))
                                : null}
                        </BulletList>
                    </InfoCard>

                    {/* SOLUCIÓN */}
                    <InfoCard $accent={C.blue}>
                        <CardLabel $color={C.blue}>LA SOLUCIÓN</CardLabel>
                        <CardTitle>Ecosistema de agentes inteligentes orquestados</CardTitle>
                        <AgentGrid>
                            {agents.map((a, i) => (
                                <AgentBadge key={i} $dot={dotColors[i % dotColors.length]}>
                                    <div className="row">
                                        <span className="dot" />
                                        {a.name}
                                    </div>
                                    <div className="desc">{a.description}</div>
                                </AgentBadge>
                            ))}
                        </AgentGrid>
                        <CommBanner>
                            <SwapOutlined />
                            Comunicación ACL + eventos · Blackboard compartido
                        </CommBanner>
                    </InfoCard>

                    {/* POWERED BY */}
                    <InfoCard $accent={C.green} $dark>
                        <CardLabel $color={C.green}>POWERED BY</CardLabel>
                        <CardTitle $light>Microsoft Azure Platform</CardTitle>
                        <div
                            style={{
                                fontStyle: "italic",
                                fontSize: 12,
                                color: "rgba(255,255,255,0.7)",
                                marginBottom: 12,
                            }}
                        >
                            100% sobre la nube de Microsoft
                        </div>
                        <PoweredItem $accent={C.blue}>
                            <div className="name">Azure OpenAI + AI Foundry</div>
                            <div className="desc">Modelos LLM y orquestación</div>
                        </PoweredItem>
                        <PoweredItem $accent={C.orange}>
                            <div className="name">SAP RFC Connector</div>
                            <div className="desc">Integración nativa con SAP</div>
                        </PoweredItem>
                        <PoweredItem $accent={C.green}>
                            <div className="name">Microsoft Fabric + Power BI</div>
                            <div className="desc">Datos, blackboard y BI</div>
                        </PoweredItem>
                        <PoweredItem $accent={C.purple}>
                            <div className="name">Cosmos DB</div>
                            <div className="desc">Estado y memoria de agentes</div>
                        </PoweredItem>
                    </InfoCard>
                </ThreeCols>
            </Slide1Body>
            <FooterBar />
        </SlideFrame>
    );
}

function Slide2({ data }) {
    // Build comparative rows from before/after
    const beforeMap = {};
    (data.metricsBefore || []).forEach((m) => (beforeMap[m.metric] = m));
    const rows = (data.metricsAfter || []).map((after) => {
        const before = beforeMap[after.metric] || {};
        return {
            metric: after.metric,
            before: before.volume || "—",
            after: after.volume || "—",
            variation: after.variation || "—",
        };
    });

    // KPI cards: tomar las primeras 3 métricas
    const kpis = rows.slice(0, 3);
    const kpiAccents = [C.blue, C.green, C.orange];

    return (
        <SlideFrame>
            <Slide2Header>
                <div>
                    <Eyebrow style={{ color: C.orange }}>
                        MÉTRICAS DE IMPACTO · RESULTADOS ESPERADOS
                    </Eyebrow>
                    <SlideTitle style={{ color: "#fff", marginTop: 6 }}>
                        {data.title.split("-")[0].trim()} — Impacto
                    </SlideTitle>
                </div>
                <div>
                    <Pill $bg={C.green}>ANTES vs. AHORA</Pill>
                    <div
                        style={{
                            textAlign: "right",
                            color: C.blue,
                            fontSize: 11,
                            marginTop: 8,
                        }}
                    >
                        Ficha 2 / 2
                    </div>
                </div>
            </Slide2Header>

            <Slide2Body>
                <TableCard>
                    <TableHead>
                        <div>MÉTRICA</div>
                        <div className="antes">ANTES</div>
                        <div className="ahora">AHORA</div>
                        <div className="var">VARIACIÓN</div>
                    </TableHead>
                    {rows.length === 0 ? (
                        <TableRow>
                            <div className="metric" style={{ color: C.textSoft }}>
                                Sin métricas registradas
                            </div>
                            <div />
                            <div />
                            <div />
                        </TableRow>
                    ) : (
                        rows.map((r, i) => (
                            <TableRow
                                key={i}
                                $negative={r.variation && r.variation.startsWith("-")}
                            >
                                <div className="metric">
                                    <CheckOutlined style={{ color: C.orange }} />
                                    {r.metric}
                                </div>
                                <div className="antes">{r.before}</div>
                                <div className="ahora">{r.after}</div>
                                <div className="var">{r.variation}</div>
                            </TableRow>
                        ))
                    )}
                </TableCard>

                <KpiRow>
                    {kpis.map((k, i) => (
                        <KpiCard key={i} $accent={kpiAccents[i % kpiAccents.length]}>
                            <div className="big">{k.variation || k.after}</div>
                            <div className="lbl">{k.metric}</div>
                            <div className="sub">
                                De {k.before} a {k.after}
                            </div>
                        </KpiCard>
                    ))}
                    {kpis.length === 0 && (
                        <KpiCard $accent={C.blue}>
                            <div className="big">—</div>
                            <div className="lbl">Sin KPIs</div>
                            <div className="sub">Capturar métricas after</div>
                        </KpiCard>
                    )}
                </KpiRow>
            </Slide2Body>

            <Slide2Footer>
                <CloudOutlined style={{ fontSize: 18 }} />
                <div>
                    <div>Construido 100% sobre Microsoft Azure</div>
                    <div className="stack">
                        Azure OpenAI · AI Foundry · SAP RFC · Cosmos DB · AKS
                    </div>
                </div>
            </Slide2Footer>
        </SlideFrame>
    );
}

export function ProjectSheetModal({ open, onClose, workshopId, data: dataProp }) {
    const carouselRef = useRef(null);

    const [fetched, setFetched] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Nueva URL del endpoint
    const fetchSheet = useCallback(async () => {
        if (!workshopId) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://app-service-backend-g8ctbsasafe3b7gt.eastus2-01.azurewebsites.net/project-sheet/workshop/${workshopId}`,
                {
                    method: "GET",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            const result = response.status === 204 ? null : await response.json();
            // Adaptar campos de agentes y métricas si es necesario
            setFetched(mapProjectSheetData(result));
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [workshopId]);

    useEffect(() => {
        if (open && workshopId && !dataProp) {
            fetchSheet();
        }
    }, [open, workshopId, dataProp, fetchSheet]);

    // Si viene por prop, también lo mapeamos
    const data = dataProp ? mapProjectSheetData(dataProp) : fetched;

    // Mapeo para adaptar campos del nuevo backend
    function mapProjectSheetData(raw) {
        if (!raw) return raw;
        // Agentes: backend usa agentName, objective, activity, inputs, outputs
        let agents = raw.agents || [];
        if (agents.length && agents[0].agentName) {
            agents = agents.map(a => ({
                name: a.agentName,
                description: a.objective,
                activity: a.activity,
                inputs: a.inputs,
                outputs: a.outputs,
            }));
        }
        // Métricas: backend ya usa metricsBefore/metricsAfter
        return {
            ...raw,
            agents,
        };
    }

    return (
        <StyledModal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1600}
            centered
            destroyOnClose
            styles={{ body: { padding: 0 } }}
        >
            {loading && !data ? (
                <div
                    style={{
                        minHeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Spin size="large" tip="Cargando ficha del proyecto..." />
                </div>
            ) : error && !data ? (
                <div
                    style={{
                        minHeight: 400,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Empty
                        description={`No se pudo cargar la ficha del proyecto${error?.message ? `: ${error.message}` : ""
                            }`}
                    />
                </div>
            ) : !data ? (
                <div
                    style={{
                        minHeight: 400,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Empty description="Sin información disponible" />
                </div>
            ) : (
                <CarouselWrapper>
                    <NavBtn $side="left" onClick={() => carouselRef.current?.prev()}>
                        <LeftOutlined />
                    </NavBtn>
                    <NavBtn $side="right" onClick={() => carouselRef.current?.next()}>
                        <RightOutlined />
                    </NavBtn>
                    <div style={{ width: "100%" }}>
                        <Carousel
                            ref={carouselRef}
                            dots
                            autoplay={false}
                            adaptiveHeight
                            infinite={false}
                        >
                            <div className="slide-shell">
                                <Slide1 data={data} />
                            </div>
                            <div className="slide-shell">
                                <Slide2 data={data} />
                            </div>
                        </Carousel>
                    </div>
                </CarouselWrapper>
            )}
        </StyledModal>
    );
}

export default ProjectSheetModal;
